import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";
import PropTypes from "prop-types";

import { useSelector } from "react-redux";
import { dispatch } from "../../../state/store";
import { clearDeletedGroups } from "../../../state/ducks";

import {
  alignItems,
  appUserRoles,
  channelOptions,
  intelligentDashboardAvailabilityRoles,
  routePaths,
  strCode
} from "../../../consts";
import { log, notificationService, t } from "../../../services";
import { arrayUtil, dropdownUtil, objectUtil, groupUtils, userUtil } from "../../../utils";
import { rolesConfigUtils } from "./utils/rolesConfigUtils";
import { adminRolesConfigUtils } from "./utils/adminRolesConfigUtils";

import {
  DropdownInput,
  FlexContainers,
  IconButton,
  Input,
  Paragraph2,
  SVG,
  Switcher,
  WrapperContainer
} from "../../../components";
import { FormSection } from "../../../templates";
import {
  AdminSectionWrapper,
  ButtonsWrapper,
  ContentOverflowWrapper,
  FormButton,
  InputSectionWrapper,
  UserFormGridContainer,
  UserFormWrapper
} from "./styled";
import RolesList from "./components/RolesList/RolesList";
import { ChangeHistory } from "./components/ChangeHistory/ChangeHistory";
import { AdminRoleSettings } from "./components/AdminRoleSettings/AdminRoleSettings";
import { Title } from "../../../templates/FormSection/styled";

const rolesInfoUrl =
  "https://dce-confluence.app.pconnect.biz/pages/viewpage.action?spaceKey=CCIT&title=00+-+IAP+-+User+Roles+Access";

// eslint-disable-next-line no-useless-escape
const emailRegex = /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/;

const NewUserForm = ({
  isAdminUser,
  usersData,
  changeHistory,
  changeHistoryLoading,
  setGroupSettingsModalActive,
  onSaveUser,
  userLoading
}) => {
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [email, setEmail] = useState("");
  const [selectedChannel, setSelectedChannel] = useState(null);

  const [selectedGroups, setSelectedGroups] = useState([]);
  const [canUseIntelligentDashboards, setCanUseIntelligentDashboards] = useState(false);
  const [intelligentDashboardsCheckboxEnabled, setIntelligentDashboardsCheckboxEnabled] =
    useState(false);

  const [rolesConfig, setRolesConfig] = useState([]);
  const [adminRolesConfig, setAdminRolesConfig] = useState();

  const [isValid, setIsValid] = useState(true);
  const [isAdminRolesValid, setIsAdminRolesValid] = useState(true);
  const [isSameData, setIsSameData] = useState(false);
  const [showErrors, setShowErrors] = useState(false);
  const [missingFields, setMissingFields] = useState({});
  const [duplicatedRolesErrors, setDuplicatedRolesErrors] = useState([]);

  const [loading, setLoading] = useState(false);

  const showHistory = useMemo(() => {
    return usersData;
  }, [usersData]);

  const history = useHistory();

  const groups = useSelector((store) => store.userGroups?.groups);
  const deletedGroups = useSelector((store) => store.userGroups?.deletedGroups);
  const groupOptions = useMemo(() => {
    return groups
      ? groups.map((x) => {
          return {
            value: x.name,
            id: x.id
          };
        })
      : [];
  }, [groups]);

  const selectedGroupOptions = useMemo(
    () =>
      selectedGroups.map((x) => {
        return { id: x, value: groupOptions.find((option) => option.id === x)?.value ?? x };
      }),
    [groupOptions, selectedGroups]
  );

  const hasReporterRole = useMemo(() => {
    const roles = adminRolesConfig?.roles ?? [];

    return roles?.findIndex((role) => role.id === appUserRoles.Reporter) !== -1;
  }, [adminRolesConfig]);

  useEffect(() => {
    const roles = adminRolesConfig?.roles ?? [];

    const isIntelligentDashboardAvailable = roles.some((role) =>
      intelligentDashboardAvailabilityRoles.includes(role.id)
    );

    setIntelligentDashboardsCheckboxEnabled(isIntelligentDashboardAvailable);
  }, [adminRolesConfig]);

  useEffect(() => {
    const roles = adminRolesConfig?.roles ?? [];
    const toggledDashboardsRoles = [appUserRoles.MarketAdmin];

    const toggleDashboards =
      hasReporterRole || roles.some((role) => toggledDashboardsRoles.includes(role.id));

    if (!usersData) {
      setCanUseIntelligentDashboards(toggleDashboards);
    }

    if (hasReporterRole) {
      setCanUseIntelligentDashboards(true);
    }
  }, [hasReporterRole, adminRolesConfig, usersData]);

  const getUserData = useCallback(() => {
    let newData = {
      firstName: name,
      lastName: surname,
      email
    };

    if (isAdminUser) {
      newData = {
        ...newData,
        ...adminRolesConfigUtils.getUserData(adminRolesConfig),
        canUseIntelligentDashboards:
          canUseIntelligentDashboards && intelligentDashboardsCheckboxEnabled
      };
    } else {
      newData.channel = selectedChannel?.id ?? null;
      newData.groups = rolesConfigUtils.getUserGroups(rolesConfig);
    }

    return newData;
  }, [
    email,
    name,
    surname,
    selectedChannel,
    canUseIntelligentDashboards,
    intelligentDashboardsCheckboxEnabled,
    rolesConfig,
    adminRolesConfig
  ]);

  const getOriginalUserData = useCallback(() => {
    let originalData = {
      firstName: usersData?.firstName,
      lastName: usersData?.lastName,
      email: usersData?.email
    };

    if (isAdminUser) {
      originalData = {
        ...originalData,
        roles: usersData?.roles ?? [],
        appTypes: usersData?.appTypes ?? [],
        markets: usersData?.markets ?? [],
        canUseIntelligentDashboards: usersData?.canUseIntelligentDashboards,
        activeFrom: usersData?.activeFrom,
        activeTo: usersData?.activeTo
      };
    } else {
      originalData.channel = usersData?.channel ?? null;
      originalData.groups = usersData?.groups?.map((config) => {
        return { ...config, locationId: config?.locationId ?? null };
      });
    }

    return originalData;
  }, [usersData]);

  useEffect(() => {
    prepopulateData();
  }, [usersData]);

  useEffect(() => {
    if (deletedGroups.length > 0) {
      setSelectedGroups(selectedGroups.filter((x) => !deletedGroups.includes(x)));
      dispatch(clearDeletedGroups());
    }
  }, [deletedGroups]);

  useEffect(() => {
    let valid = !!name && !!surname && !!email && emailRegex.test(email);
    let isSame = false;

    const errorFields = {
      name: !name,
      surname: !surname,
      email: !email || !emailRegex.test(email),
      channel: !isAdminUser && !selectedChannel
    };

    if (isAdminUser) {
      const { isValid, invalidFields } = adminRolesConfigUtils.validateConfig(adminRolesConfig);

      if (!isValid) {
        valid = false;
      }
      errorFields.AdminRolesConfig = invalidFields;
      setMissingFields(errorFields);
      setIsAdminRolesValid(isValid);
    } else {
      valid = valid && !!selectedChannel;

      const rolesConfigMissingFields = rolesConfigUtils.validateConfig(rolesConfig);
      const roleDuplicateErrors = rolesConfigUtils.getRolesConfigDuplicateErrors(rolesConfig);

      onUpdateDuplicatedRoleErrors(roleDuplicateErrors);
      setDuplicatedRolesErrors(roleDuplicateErrors);

      errorFields.rolesConfig = rolesConfigMissingFields;
      setMissingFields(errorFields);

      if (rolesConfigMissingFields?.length || roleDuplicateErrors?.length) {
        valid = false;
      }
    }

    if (usersData) {
      const originalData = getOriginalUserData();
      const newData = getUserData();

      if (
        objectUtil.objEquals(originalData, newData) &&
        arrayUtil.arrayEquals(usersData?.groupAssignments ?? [], selectedGroups)
      ) {
        valid = false;
        isSame = true;
      }
    }

    setIsValid(valid);
    setIsSameData(isSame);
  }, [
    name,
    surname,
    email,
    selectedChannel,
    canUseIntelligentDashboards,
    rolesConfig,
    adminRolesConfig,
    selectedGroups,
    usersData?.groupAssignments
  ]);

  const prepopulateData = useCallback(() => {
    if (usersData) {
      setName(usersData?.firstName);
      setSurname(usersData?.lastName);
      setEmail(usersData?.email);
      setCanUseIntelligentDashboards(usersData?.canUseIntelligentDashboards);
      setSelectedGroups(usersData?.groupAssignments ?? []);

      if (isAdminUser) {
        const rolesConfig = adminRolesConfigUtils.transformUserData(usersData);
        setAdminRolesConfig(rolesConfig);
      } else {
        if (usersData?.channel) {
          setSelectedChannel(channelOptions.find((opt) => opt.id === usersData.channel));
        }

        const rolesConfig = rolesConfigUtils.tranformGroups(usersData?.groups);
        setRolesConfig(rolesConfig);
      }
    } else if (isAdminUser) {
      setAdminRolesConfig({});
    }
  }, [usersData, groupOptions]);

  const resetFormData = () => {
    setName("");
    setSurname("");
    setEmail("");
    setSelectedChannel(null);
    setCanUseIntelligentDashboards(false);
    setRolesConfig([]);
    setAdminRolesConfig();
    setSelectedGroups([]);
  };

  const onUpdateDuplicatedRoleErrors = (roleDuplicateErrors) => {
    roleDuplicateErrors.forEach((errorConfig) => {
      let showNotification = false;

      const existingError = duplicatedRolesErrors.find(
        (error) => error.application === errorConfig.application && error.role === errorConfig.role
      );

      if (existingError) {
        const filteredIds = errorConfig?.configIds?.filter(
          (id) => !existingError.configIds.includes(id)
        );

        if (filteredIds.length) {
          showNotification = true;
        }
      } else {
        showNotification = true;
      }

      if (showNotification) {
        notificationService.showError({
          title: t(strCode.ROLE_DUPLICATION_ERROR_TITLE),
          message: t(strCode.ROLE_DUPLICATION_ERROR_MESSAGE, {
            role: errorConfig.role,
            application: errorConfig.application
          })
        });
      }
    });

    setDuplicatedRolesErrors(roleDuplicateErrors);
  };

  const onClose = () => {
    resetFormData();
    history.push(routePaths.USERS);
  };

  const onSubmit = async () => {
    if (!showErrors) {
      setShowErrors(true);
    }

    if (isSameData) {
      onClose();
      return;
    }

    if (!isValid) {
      log.warn("NewUserForm: Unable to save user due to missing fields");
      notificationService.showError({
        title: t(strCode.MISSING_FIELDS),
        message: t(strCode.MISSING_FIELDS_MESSAGE)
      });
      return;
    }

    setLoading(true);
    const user = getUserData();
    try {
      await onSaveUser(userUtil.prepareUserData(user, isAdminUser), selectedGroups);
      resetFormData();
    } catch (e) {
      log.error(`An error occurred while saving users data.`, e);
    } finally {
      setLoading(false);
    }
  };

  const groupSelector = (
    <DropdownInput
      id="group-selector"
      className="group-selector"
      title={t(strCode.USER_GROUP_TITLE)}
      placeholder={t(strCode.USER_GROUP_TITLE)}
      options={groupOptions}
      selectedOptions={selectedGroupOptions}
      onSelectOption={(id) => {
        setSelectedGroups(
          dropdownUtil
            .updateSelectedOptions(id, selectedGroupOptions, groupOptions, true, true)
            .map((x) => x.id)
        );
      }}
      onOptionsFiltered={(filteredOptions, searchText) =>
        groupUtils.getFilteredOptions(filteredOptions, searchText)
      }
      isSearchable={true}
      useChips={true}
      isRequired={false}
      InputComponent={
        <WrapperContainer Container={FlexContainers.FlexRow} gap={12}>
          <Paragraph2>{t(strCode.OPTIONAL_INFO)}</Paragraph2>
          <IconButton
            onClick={(e) => {
              if (!e.target.closest(".group-selector").classList.contains("active")) {
                e.stopPropagation();
              }
              setGroupSettingsModalActive(true);
            }}
            tooltipLabel={t(strCode.GROUP_SETTINGS_TOOLTIP)}>
            <SVG.GearIcon />
          </IconButton>
        </WrapperContainer>
      }
    />
  );

  return (
    <WrapperContainer
      Container={FlexContainers.FlexColumnStart}
      alignItems={alignItems.start}
      width={"100%"}
      height={"100%"}>
      <WrapperContainer
        Container={FlexContainers.FlexColumnBetween}
        width={"100%"}
        height={"100%"}
        pt={36}>
        {loading || userLoading ? (
          <WrapperContainer
            Container={FlexContainers.FlexColumnCenter}
            width={"100%"}
            mt={200}
            textAlign={"center"}>
            <Paragraph2>{t(strCode.LOADING)}</Paragraph2>
          </WrapperContainer>
        ) : (
          <>
            <WrapperContainer width={"100%"} height={"100%"}>
              <ContentOverflowWrapper isNewUser={!usersData}>
                <UserFormWrapper
                  Container={FlexContainers.FlexColumnStart}
                  alignItems={alignItems.start}
                  width={"100%"}
                  gap={16}>
                  <UserFormGridContainer
                    Container={FlexContainers.FlexRow}
                    gap={16}
                    showHistory={showHistory}>
                    <FormSection title={t(strCode.BASIC_INFO_TITLE)}>
                      <InputSectionWrapper Container={FlexContainers.FlexRowStart} mb={18}>
                        <Input
                          id={"name"}
                          title={t(strCode.NAME_TITLE)}
                          value={name}
                          setValue={setName}
                          placeholder={t(strCode.NAME_TITLE)}
                          hasError={showErrors && missingFields?.name}
                        />

                        <Input
                          id={"surname"}
                          title={t(strCode.SURNAME_TITLE)}
                          value={surname}
                          setValue={setSurname}
                          placeholder={t(strCode.SURNAME_TITLE)}
                          hasError={showErrors && missingFields?.surname}
                        />
                      </InputSectionWrapper>

                      <InputSectionWrapper Container={FlexContainers.FlexRowStart} mb={18}>
                        <Input
                          id={"email"}
                          title={t(strCode.EMAIL_TITLE)}
                          value={email}
                          setValue={setEmail}
                          placeholder={t(strCode.EMAIL_TITLE)}
                          disabled={!!usersData}
                          hasError={showErrors && missingFields?.email}
                        />

                        {isAdminUser ? (
                          groupSelector
                        ) : (
                          <DropdownInput
                            id="channel-selector"
                            title={t(strCode.CHANNEL_TITLE)}
                            placeholder={t(strCode.SELECT_PLACEHOLDER)}
                            options={channelOptions}
                            selectedOption={selectedChannel}
                            onSelectOption={(id) => {
                              setSelectedChannel(channelOptions.find((opt) => opt.id === id));
                            }}
                            hasError={showErrors && missingFields?.channel}
                          />
                        )}
                      </InputSectionWrapper>

                      {!isAdminUser ? (
                        <InputSectionWrapper Container={FlexContainers.FlexRowStart} mb={18}>
                          {groupSelector}
                        </InputSectionWrapper>
                      ) : null}
                    </FormSection>

                    {showHistory && (
                      <ChangeHistory
                        changeHistory={changeHistory}
                        loading={changeHistoryLoading}
                        isAdminUser={isAdminUser}
                      />
                    )}
                  </UserFormGridContainer>

                  {isAdminUser ? (
                    <FormSection
                      Component={
                        <AdminSectionWrapper
                          Container={FlexContainers.FlexRowBetween}
                          width={"100%"}>
                          <WrapperContainer Container={FlexContainers.FlexRow} gap={8}>
                            <Title hasError={showErrors && !isAdminRolesValid}>
                              {t(strCode.ROLE_SETTINGS_TITLE)}
                            </Title>
                            <IconButton
                              onClick={() => {
                                window.open(rolesInfoUrl, "_blank", "noopener noreferrer");
                              }}
                              tooltipLabel={t(strCode.ROLES_LINK_INFO)}>
                              <SVG.NavigateToAppLinkIcon />
                            </IconButton>
                          </WrapperContainer>
                          <Switcher
                            disabled={!intelligentDashboardsCheckboxEnabled || hasReporterRole}
                            checked={
                              canUseIntelligentDashboards && intelligentDashboardsCheckboxEnabled
                            }
                            onSwitch={() => setCanUseIntelligentDashboards((prev) => !prev)}
                            title={t(strCode.SMART_DASHBOARDS)}
                          />
                        </AdminSectionWrapper>
                      }>
                      {adminRolesConfig && (
                        <AdminRoleSettings
                          rolesConfig={adminRolesConfig}
                          setRolesConfig={setAdminRolesConfig}
                        />
                      )}
                    </FormSection>
                  ) : (
                    <RolesList
                      values={rolesConfig}
                      setValues={setRolesConfig}
                      missingFields={showErrors ? missingFields?.rolesConfig : []}
                      roleDuplicateErrors={duplicatedRolesErrors}
                    />
                  )}
                </UserFormWrapper>
              </ContentOverflowWrapper>
            </WrapperContainer>

            <ButtonsWrapper Container={FlexContainers.FlexRowEnd} width="100%" gap={8} mt={11}>
              <FormButton secondary onClick={onClose}>
                {t(strCode.CANCEL_BTN)}
              </FormButton>
              <FormButton onClick={onSubmit}>
                {usersData ? t(strCode.SAVE_CHANGES_BTN) : t(strCode.SUBMIT_BTN)}
              </FormButton>
            </ButtonsWrapper>
          </>
        )}
      </WrapperContainer>
    </WrapperContainer>
  );
};

NewUserForm.propTypes = {
  isAdminUser: PropTypes.bool,
  usersData: PropTypes.object,
  changeHistory: PropTypes.arrayOf(PropTypes.object),
  changeHistoryLoading: PropTypes.bool,
  onSaveUser: PropTypes.func.isRequired,
  userLoading: PropTypes.bool,
  setGroupSettingsModalActive: PropTypes.func
};

export default NewUserForm;
